/**
 * `scheduling` — availability, slots, holds and calls.
 *
 * `mockups/client/calls.html` is the page these shapes are cut to: the grid of
 * 45-minute slots in the client's own zone, the "Hours and booking rules" fold in
 * both zones, and the timeline of past calls with their notes and the actions still
 * open from each.
 *
 * Times on the wire are RFC 3339 with an offset and the API speaks UTC
 * (`.docs/00-index.md` §8). A *rule* is a wall time in the studio's zone and is
 * carried as `HH:MM`, because 10:00 on one day is a different instant from 10:00 on
 * the next — and, in a zone that moves its clocks, a different offset as well — and a
 * rule is not an instant.
 *
 * Two things here are worth reading before the schemas.
 *
 *   **A call has two states, not three.** `.docs/16-files-calls-reports.md` §B lists
 *   `booked | cancelled | held`, and `held` is a state nothing in this design can
 *   write: a hold is a `slot_hold` row with a ten-minute TTL and no reference, and a
 *   `call` row exists only once the calendar event does. A third value would be a
 *   state no transition reaches and no sweep clears. The session report records it.
 *
 *   **The grid is anchored in the client's zone, not the studio's.** The studio's day
 *   is the whole twenty-four hours of its own zone, because it keeps no local office;
 *   what a client is shown is `client_earliest_local` / `client_latest_local`, which
 *   are 08:00 and 20:00 in **their** zone. The offer window is the intersection of
 *   the two, and the grid steps from **its** start — so a client always reads a grid
 *   that runs 08:00 to 20:00 of their own day.
 */
import { z } from 'zod';
import { ClientSlugSchema } from './refs.js';
import { DateOnlySchema, MoneySchema, Rfc3339Schema } from './primitives.js';
/* ------------------------------------------------------------------- the states */
/**
 * What a call can be. See the header for why `held` is not one of them.
 *
 * Not a `states.ts` machine: there is one transition, `booked → cancelled`, and a
 * machine for it would be a table with one row. The guard is written as the states a
 * call may still move **from** (`CALL_LIVE_STATES`), which is the shape `CLAUDE.md`
 * §Gotchas asks for — a list of states to ignore is how `on_account` and `refunded`
 * fell through session 15's receipt.
 */
export const CALL_STATES = ['booked', 'cancelled'];
/** The states a call can still be moved out of. Everything else has been decided. */
export const CALL_LIVE_STATES = ['booked'];
/** Whose action an open item from a call is. The mockup prints "You:" and "Us:". */
export const CALL_ACTION_OWNERS = ['client', 'studio'];
/** Which side of the table an attendee sits. Snapshotted onto the call. */
export const ATTENDEE_SIDES = ['client', 'studio'];
/* -------------------------------------------------------------- wall times */
/** `10:00`. Twenty-four hour, zero padded, minutes only — no seconds, no zone. */
export const LOCAL_TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
export const LocalTimeSchema = z
    .string()
    .regex(LOCAL_TIME_PATTERN, 'A local time is HH:MM on a 24-hour clock, for example 10:00.')
    .describe('A wall-clock time of day, HH:MM, 24-hour. It carries no zone: the rule it belongs to ' +
    'says which zone reads it.');
/** The `{ hour, minute }` an `HH:MM` string means. Throws for anything else. */
export function parseLocalTime(value) {
    const match = LOCAL_TIME_PATTERN.exec(value);
    if (match === null) {
        throw new RangeError(`"${value}" is not a local time — a local time is HH:MM.`);
    }
    return { hour: Number(match[1]), minute: Number(match[2]) };
}
/** Minutes since midnight, for comparing two wall times without building a date. */
export function localTimeMinutes(value) {
    const { hour, minute } = parseLocalTime(value);
    return hour * MINUTES_PER_HOUR + minute;
}
const MINUTES_PER_HOUR = 60;
/* ------------------------------------------------------- when a day closes */
/**
 * Midnight at the **end** of a day, which `HH:MM` cannot say.
 *
 * `00:00` is the start of a day and `23:59` is a minute short of its end, so neither
 * writes down "this day is open all the way through". The studio is distributed and
 * keeps no local office hours: its working day is the whole twenty-four, and a window
 * that stopped at 23:59 would leave a one-minute hole at every midnight for a client
 * far enough east or west to be offered slots across it.
 */
export const END_OF_DAY = '24:00';
/** Twenty-four hours in minutes. `closingTimeMinutes(END_OF_DAY)`. */
export const MINUTES_PER_DAY = 1_440;
/** `LOCAL_TIME_PATTERN`, plus `24:00`. Closing times only — never a start. */
export const CLOSING_TIME_PATTERN = /^(([01]\d|2[0-3]):([0-5]\d)|24:00)$/;
export const ClosingTimeSchema = z
    .string()
    .regex(CLOSING_TIME_PATTERN, 'A closing time is HH:MM on a 24-hour clock, or 24:00 for the end of the day.')
    .describe('When a window closes: a wall-clock time of day, HH:MM, 24-hour — or 24:00, which is ' +
    'midnight at the end of that day rather than the start of it. It carries no zone.');
/**
 * The `{ hour, minute }` a closing time means. `24:00` is hour **24**, deliberately.
 *
 * `Date.UTC(y, m, d, 24, 0)` is midnight at the start of the next day, so
 * `instantAtWallTime` turns hour 24 into exactly the instant the day ends without a
 * special case — and the two-pass offset correction there still reads the right side
 * of a clock change. Throws for anything `CLOSING_TIME_PATTERN` refuses.
 */
export function parseClosingTime(value) {
    if (value === END_OF_DAY)
        return { hour: 24, minute: 0 };
    if (!CLOSING_TIME_PATTERN.test(value)) {
        throw new RangeError(`"${value}" is not a closing time — a closing time is HH:MM or 24:00.`);
    }
    return parseLocalTime(value);
}
/** Minutes since midnight, `24:00` being 1,440. The comparable form of a closing time. */
export function closingTimeMinutes(value) {
    const { hour, minute } = parseClosingTime(value);
    return hour * MINUTES_PER_HOUR + minute;
}
/* ------------------------------------------------------------- the call type */
export const CallTypeSchema = z
    .object({
    code: z
        .string()
        .describe('The call type’s code — monthly_review, quick_question, consulting_session.'),
    name: z.string().describe('What it is called on the booking form — "Monthly review".'),
    minutes: z.int().describe('How long a call of this kind runs by default.'),
    billable: z
        .boolean()
        .describe('True when the time is charged for. Reporting and coordination calls are not.'),
    requires_extra: z
        .boolean()
        .describe('True when booking one needs the reference of an approved extra. Only a billable ' +
        'type has this, and it is what stops a client booking work nobody agreed to pay for.'),
    note: z
        .string()
        .describe('The line the booking form prints beside it — "45 min, included". May be empty.'),
})
    .describe('One kind of call a client may book.');
/* ------------------------------------------------------------ booking rules */
/**
 * The "Hours and booking rules" fold, in **both** zones.
 *
 * `mockups/client/calls.html` prints our hours, our zone, the reader's zone, the
 * overlap in the reader's time, the notice, the buffer and the horizon — so the shape
 * carries all seven and the page composes none of them.
 */
export const BookingRulesSchema = z
    .object({
    studio_timezone: z.string().describe('The studio’s IANA zone — UTC.'),
    studio_hours: z
        .array(z.object({
        weekday: z.int().describe('0 for Sunday through 6 for Saturday, as JavaScript counts.'),
        start_local: z.string().describe('When the studio opens that day, HH:MM, its own zone.'),
        end_local: z
            .string()
            .describe('When it closes, HH:MM, its own zone — 24:00 for the end of the day.'),
    }))
        .describe('The studio’s working windows, one per weekday it works, Monday first.'),
    studio_utc_offset_minutes: z
        .int()
        .describe('How far ahead of UTC the studio is on the day this was read — 0 for the ' +
        '"UTC+0" the calls page prints beside UTC.'),
    your_timezone: z
        .string()
        .describe('The zone the grid is drawn in: the reader’s own, from their member row.'),
    your_utc_offset_minutes: z
        .int()
        .describe('How far ahead of UTC the reader is on that day — −240 for the "UTC−4" the ' +
        'page prints beside America/New_York in summer, and −300 after 1 November.'),
    overlap_start_local: z
        .string()
        .describe('The earliest the studio’s day reaches in your zone, HH:MM — "00:00" for a studio ' +
        'that works the whole day, which is one that keeps no local office hours.'),
    overlap_end_local: z
        .string()
        .describe('The latest it reaches in your zone, HH:MM — or "24:00" for the end of it.'),
    earliest_local: z
        .string()
        .describe('The earliest hour a slot is ever offered in your zone, HH:MM — "08:00".'),
    latest_local: z.string().describe('The latest a slot may end in your zone, HH:MM — "19:00".'),
    slot_minutes: z.int().describe('The step the grid moves in — 45.'),
    buffer_minutes: z.int().describe('Clear time kept either side of every call — 15.'),
    min_notice_hours: z.int().describe('How far ahead a booking has to be made — 24.'),
    horizon_days: z.int().describe('How far ahead the calendar can be booked — 21.'),
})
    .describe('The hours and rules a booking obeys, in the studio’s zone and in yours.');
/* ------------------------------------------------------------------- slots */
/** Why a slot on the grid cannot be taken. Null when it can. */
export const SLOT_BLOCKERS = ['busy', 'held', 'notice', 'horizon'];
export const SlotSchema = z
    .object({
    start_at: Rfc3339Schema.describe('When the slot starts, in UTC.'),
    end_at: Rfc3339Schema.describe('When it ends, in UTC. The call type decides how long.'),
    local_time: z
        .string()
        .describe('The start as the grid prints it in your zone, HH:MM — "08:00".'),
    available: z.boolean().describe('True when it can be held and booked.'),
    blocked_by: z
        .enum(SLOT_BLOCKERS)
        .nullable()
        .describe('Why it cannot: busy (a call or a calendar event, plus the buffer), held (somebody ' +
        'is part-way through booking it), notice (too soon) or horizon (too far off). ' +
        'Null when it is available.'),
})
    .describe('One slot on the booking grid, in UTC and in the reader’s own local time.');
/**
 * One column of the grid.
 *
 * There is no `weekday_label`: the column head on `mockups/client/calls.html` is
 * "Mon 31", and rendering a date is `useFormat()`'s (`.docs/00-index.md` §5, "Money/
 * date rendering in the SPA"). `date` is what the page formats. `local_time` on a slot
 * is the exception and is here on purpose — the anchoring of the grid is the API's
 * decision, and `08:00` is that decision made visible.
 */
export const AvailabilityDaySchema = z
    .object({
    date: DateOnlySchema.describe('The day, in **your** zone — that is what the grid columns are.'),
    slots: z.array(SlotSchema).describe('The day’s slots, earliest first. Empty for a closed day.'),
})
    .describe('One column of the booking grid.');
export const AvailabilitySchema = z
    .object({
    call_type: CallTypeSchema.describe('The kind of call these slots are for.'),
    week_start: DateOnlySchema.describe('The Monday of the week shown, in your zone.'),
    week_end: DateOnlySchema.describe('The Sunday of the week shown, in your zone.'),
    timezone: z.string().describe('The zone `local_time` and `date` are in.'),
    days: z.array(AvailabilityDaySchema).describe('One entry per day the studio works.'),
    rules: BookingRulesSchema.describe('The rules that produced this grid.'),
})
    .describe('A week of the booking grid, as `mockups/client/calls.html` draws it.');
/* -------------------------------------------------------------- the hold */
export const SlotHoldSchema = z
    .object({
    hold_id: z.uuid().describe('Pass it to the booking call. It is not a reference; it expires.'),
    start_at: Rfc3339Schema.describe('The slot it holds, in UTC.'),
    end_at: Rfc3339Schema.describe('When the held slot ends, in UTC.'),
    call_type: z.string().describe('The call type code the hold was taken for.'),
    expires_at: Rfc3339Schema.describe('When it lapses and the slot goes back on the grid. Ten minutes from when it was taken.'),
})
    .describe('A slot held while somebody finishes booking it.');
/* -------------------------------------------------------------- the call */
export const CallAttendeeSchema = z
    .object({
    name: z.string().describe('The person, as everyone says it — "Deniz Aksoy".'),
    email: z.string().describe('Where the invitation went.'),
    side: z.enum(ATTENDEE_SIDES).describe('client for their people, studio for ours.'),
})
    .describe('One person on the call.');
export const CallActionSchema = z
    .object({
    who: z
        .enum(CALL_ACTION_OWNERS)
        .describe('Whose it is: client for "You: send us the Q4 launch dates", studio for ours.'),
    text: z.string().describe('What was agreed, in one line.'),
    done: z.boolean().describe('True once it has happened.'),
})
    .describe('One thing left open by a call.');
export const CallSchema = z
    .object({
    ref: z.string().describe('The call’s reference, CALL-2026-091.'),
    client: ClientSlugSchema.describe('Whose call it is.'),
    call_type: z.string().describe('The call type code it was booked as.'),
    call_type_name: z
        .string()
        .describe('What that type was called when it was booked. Snapshot, like every price here.'),
    title: z.string().describe('What the call is about — "September planning".'),
    status: z.enum(CALL_STATES).describe('booked, or cancelled.'),
    billable: z.boolean().describe('True when the time is charged for.'),
    start_at: Rfc3339Schema.describe('When it starts, in UTC.'),
    end_at: Rfc3339Schema.describe('When it ends, in UTC.'),
    minutes: z.int().describe('How long it runs.'),
    attendees: z.array(CallAttendeeSchema).describe('Everyone on it, client side and studio side.'),
    meet_url: z
        .string()
        .nullable()
        .describe('The Google Meet link, or null when the calendar gave none.'),
    work_order_ref: z
        .string()
        .nullable()
        .describe('The approved extra a billable call is booked against — REQ-115. Null otherwise.'),
    billable_amount: MoneySchema.nullable().describe('The amount approved for the extra this call spends — $350.00 for REQ-115. Null for ' +
        'an included call.'),
    billable_approved_at: Rfc3339Schema.nullable().describe('When the client approved the extra this call spends. Null for an included call.'),
    notes: z
        .string()
        .nullable()
        .describe('What was written up afterwards. Null until somebody writes it.'),
    actions: z.array(CallActionSchema).describe('What the call left open. Empty when nothing is.'),
    cancelled_at: Rfc3339Schema.nullable().describe('When it was cancelled, or null.'),
    cancel_reason: z
        .string()
        .nullable()
        .describe('Why, in the words whoever cancelled it used. Null when there was none.'),
    created_at: Rfc3339Schema.describe('When it was booked.'),
})
    .describe('One call, booked or cancelled.');
/**
 * The client's calls page: the next one, the ones after it, and the past with notes.
 *
 * Three lists rather than one filtered list, because `mockups/client/calls.html` is
 * three different renderings — a hero with a Join button, a line under a rule, and a
 * timeline — and a page that had to split one list would be deciding "which is next"
 * in the browser.
 */
export const CallsPageSchema = z
    .object({
    next: CallSchema.nullable().describe('The soonest booked call, or null when there is none.'),
    upcoming: z
        .array(CallSchema)
        .describe('Booked calls after `next`, soonest first. `next` is not repeated here.'),
    past: z
        .array(CallSchema)
        .describe('Calls that have already happened, most recent first, with their notes.'),
    timezone: z.string().describe('The zone the page should render these in: the reader’s own.'),
})
    .describe('Everything `mockups/client/calls.html` renders about a client’s calls.');
/* ------------------------------------------------------------- the bodies */
/**
 * The five things a caller sends, and the one thing the studio sends.
 *
 * `z.strictObject` all the way down, because these are also studio-action inputs and
 * `defineAction` refuses anything looser (`CLAUDE.md` §Actions): a misspelt field has
 * to be refused by name rather than silently dropped. `.docs/00-index.md` §5 puts them
 * here so the API validates and the SPA's forms resolve with the same object.
 */
export const HoldSlotSchema = z
    .strictObject({
    slot_start: Rfc3339Schema.describe('The slot to hold, exactly as the availability grid gave it — start_at, in UTC.'),
    call_type: z.string().min(1).describe('The call type code the slot was drawn for.'),
    minutes: z
        .int()
        .min(5)
        .max(480)
        .optional()
        .describe('How long the session runs, for a **billable** type only — 120 for a two-hour ' +
        'consulting session. Left out, the type’s own length. An included call cannot be ' +
        'stretched.'),
})
    .describe('Holding a slot for ten minutes while the rest of the booking is filled in.');
export const BookCallSchema = z
    .strictObject({
    hold_id: z.uuid().describe('The hold this booking consumes, from the hold call.'),
    title: z
        .string()
        .trim()
        .min(3)
        .max(200)
        .describe('What the call is about — "September planning", "Q4 roadmap workshop".'),
    attendees: z
        .array(z.string().describe('One person’s email address.'))
        .max(20)
        .default([])
        .describe('Everybody who should be on it, by email address. Each has to be somebody with a ' +
        'seat on this account or somebody at the studio. Left empty, whoever is booking.'),
    work_order_ref: z
        .string()
        .optional()
        .describe('The approved extra a **billable** call is booked against — REQ-115. Required for a ' +
        'consulting session and refused for anything included.'),
})
    .describe('Turning a held slot into a booked call.');
export const RescheduleCallSchema = z
    .strictObject({
    hold_id: z
        .uuid()
        .describe('A hold on the new slot, taken for the same call type the call already is.'),
})
    .describe('Moving a booked call to a slot that has been held for it.');
export const CancelCallSchema = z
    .strictObject({
    reason: z
        .string()
        .trim()
        .max(2_000)
        .default('')
        .describe('Why, in your own words. Required when the call is inside the notice window — the ' +
        'other side is rearranging their day and the sentence is the whole of what they need.'),
})
    .describe('Cancelling a booked call.');
export const CallNotesSchema = z
    .strictObject({
    notes: z
        .string()
        .trim()
        .max(20_000)
        .describe('The write-up, in prose. What was walked through, what was agreed.'),
    actions: z
        .array(z.strictObject({
        who: z
            .enum(CALL_ACTION_OWNERS)
            .describe('client for something they owe us, studio for something we owe them.'),
        text: z.string().trim().min(1).max(500).describe('What was agreed, in one line.'),
        done: z.boolean().default(false).describe('True once it has happened.'),
    }))
        .max(50)
        .default([])
        .describe('What the call left open. Replaces the list; it is not appended to.'),
})
    .describe('Writing up a call.');
export const SetAvailabilityRulesSchema = z
    .strictObject({
    windows: z
        .array(z.strictObject({
        weekday: z
            .int()
            .min(0)
            .max(6)
            .describe('0 for Sunday through 6 for Saturday. Mon–Fri is 1 to 5.'),
        start_local: LocalTimeSchema.describe('When the studio opens, HH:MM, in its own zone.'),
        end_local: ClosingTimeSchema.describe('When it closes, HH:MM, in its own zone — or 24:00 for a day that runs all the ' +
            'way through, which is what a studio with no local office hours works.'),
    }))
        .min(1)
        .max(7)
        .describe('One window per weekday the studio works. A weekday left out is switched off, so ' +
        'this is the whole set rather than a change to it.'),
    slot_minutes: z
        .int()
        .min(5)
        .max(240)
        .describe('The step the grid moves in — 45. Not the length of a call.'),
    buffer_minutes: z
        .int()
        .min(0)
        .max(120)
        .describe('Clear time kept either side of anything already booked — 15.'),
    min_notice_hours: z
        .int()
        .min(0)
        .max(720)
        .describe('How far ahead a booking has to be made — 24.'),
    horizon_days: z.int().min(1).max(365).describe('How far ahead the calendar is open — 21.'),
    client_earliest_local: LocalTimeSchema.describe('The earliest a slot is ever offered, HH:MM, in the **client’s** zone — 08:00. It is ' +
        'what stops a client being offered the middle of their night.'),
    client_latest_local: LocalTimeSchema.describe('The latest a slot may end, HH:MM, in the client’s zone — 20:00. With 08:00 above it ' +
        'is the whole of what a client is ever shown: 08:00 to 20:00, their own time.'),
})
    .describe('The studio’s hours and booking rules, written as one set.');
//# sourceMappingURL=scheduling.js.map